import React, { useEffect, useState } from "react";
import { Row, Col, Image, ListGroup, ProgressBar } from "react-bootstrap";
import { BsChevronRight } from "react-icons/bs";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { getEmployeeOnboardingSteps } from "../../../services/onBoardingServices";
import iconMap from "../../../config/iconConfig";
import { getEmployeeDetails } from "../../../services/EmployeeDetailServices";
import { getFile } from "../../../utils/getFile";
import config from "../../../config";
import { decrypt, encrypt } from "../../../utils/crypto";

const EmployeeOnboardingDetails = () => {
  let { employee_id } = useParams();
  employee_id = decrypt(employee_id);
  const encryptedEmployeeId = encrypt(employee_id);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [OnboardingSteps, setOnboardingSteps] = useState([]);
  const [progress, setProgress] = useState(0);
  const [employeeDetails, setEmployeeDetails] = useState({});
  const [profileImage, setProfileImage] = useState(null);

  useEffect(() => {
    const fetchAndCalculateProgress = async () => {
      if (employee_id) {
        try {
          const result = await getEmployeeOnboardingSteps(employee_id);
          if (result?.success === true) {
            const steps = result?.OnboardingSteps || [];
            setOnboardingSteps(steps);

            const completedSteps = steps.filter(
              (step) => step.status === "COMPLETED"
            ).length;
            const totalSteps = steps.length;
            const calculatedProgress = Math.round(
              (completedSteps / totalSteps) * 100
            );
            setProgress(calculatedProgress);
          }
        } catch (error) {
          console.error(error);
        }
      }
    };

    fetchAndCalculateProgress();
  }, [employee_id]);

  const fetchImage = async (data) => {
    let image = null;
    if (data?.photo) {
      const imageUrl = await getFile(`${config.IMAGE_URL}${data.photo}`);
      if (imageUrl) {
        image = imageUrl;
      }
    }
    setProfileImage(image);
  };

  useEffect(() => {
    const fetchEmployeeDetails = async (employeeId) => {
      try {
        const response = await getEmployeeDetails(employeeId);
        if (Object.keys(response?.results)?.length > 0) {
          setEmployeeDetails(response?.results ?? {});
          fetchImage(response?.results);
        }
      } catch (error) {
        console.error(error);
      }
    };

    if (employee_id) {
      fetchEmployeeDetails(employee_id);
    }
  }, []);

  return (
    <div className="page-wrapper">
      {/* Page Content */}
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={t("Onboarding Details")}
          title={t("Onboarding")}
          subtitle={t("Details")}
        />

        <Row className="align-items-center mb-4">
          <Col xs="auto">
            {profileImage ? (
              <Image
                src={profileImage}
                roundedCircle
                style={{ width: 80, height: 80 }}
              />
            ) : (
              <div
                className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                style={{
                  width: 50,
                  height: 50,
                  fontSize: "1.5rem",
                }}
              >
                {employeeDetails?.first_name
                  ? employeeDetails?.first_name[0]
                  : ""}
                {employeeDetails?.last_name
                  ? employeeDetails?.last_name[0]
                  : ""}
              </div>
            )}
          </Col>
          <Col>
            <h5 className="fw-bold mb-1">
              {employeeDetails?.first_name}
              {employeeDetails?.middle_name
                ? " " + employeeDetails?.middle_name + " "
                : " "}
              {employeeDetails?.last_name}
            </h5>
            <div className="text-muted">
              Employee ID: {employeeDetails?.employment_id}
            </div>
          </Col>
        </Row>

        <h5 className="fw-semibold">Onboarding Progress</h5>

        <p className="text-muted mb-4">
          View and manage the onboarding process.
        </p>
        <ProgressBar
          now={progress}
          style={{ height: "6px" }}
          variant="primary"
        />
        <div className="text-muted small mt-1 mb-3">{progress}%</div>
        <ListGroup variant="flush">
          {OnboardingSteps.map((step, idx) => {
            const IconComponent = iconMap[step.stepIcon];
            const isClickable =
              step.stepOrder === 1 ||
              (idx > 0 &&
                OnboardingSteps[idx - 1].status === "COMPLETED" &&
                step.status !== "PENDING");

            const stepPath = `../onboarding/${encryptedEmployeeId}/${step.stepKey
              .toLowerCase()
              .replace(/ /g, "-")}`;
            const color =
              step.status === "PENDING"
                ? "warning"
                : step.status === "IN_PROGRESS"
                ? "info"
                : "success";
            return (
              <ListGroup.Item
                key={idx}
                className={`d-flex align-items-center justify-content-between py-3 ${
                  isClickable ? "list-group-item-action" : "disabled"
                }`}
                as={isClickable ? "button" : "div"}
                onClick={() => {
                  if (isClickable) {
                    navigate(stepPath, {
                      state: { step: step },
                    });
                  }
                }}
                style={{ cursor: isClickable ? "pointer" : "not-allowed" }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                    style={{
                      width: "40px",
                      height: "40px",
                    }}
                  >
                    {IconComponent ? (
                      <IconComponent size={20} />
                    ) : (
                      <div
                        className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                        style={{
                          width: 40,
                          height: 30,
                          fontSize: "1.5rem",
                        }}
                      >
                        {step.stepName[0]}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="fw-semibold">{step.stepName}</div>
                    <div className={`badge badge-${color}`}>{step.status}</div>
                  </div>
                </div>
                <BsChevronRight className="text-muted" />
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      </div>
    </div>
  );
};

export default EmployeeOnboardingDetails;
